import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quote } from "lucide-react";
import { API } from "../data/constants";

/**
 * Inline industry testimonial widget — fetches `/api/testimonials/featured/:slug`
 * and renders nothing if no admin-tagged testimonial exists for that industry.
 * This keeps the industry page clean during onboarding and surfaces real social
 * proof in the moment-of-decision section once content is added.
 */
export const IndustryTestimonial = ({ industrySlug }) => {
  const [t, setT] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/testimonials/featured/${industrySlug}`)
      .then(({ data }) => {
        if (cancelled) return;
        setT(data || null);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [industrySlug]);

  if (!loaded || !t) return null;

  const subtitle = [t.role, t.company].filter(Boolean).join(" · ");

  return (
    <section className="section-padding bg-white" data-testid={`industry-testimonial-${industrySlug}`}>
      <div className="max-w-4xl mx-auto">
        <div className="relative bg-gradient-to-br from-orange-light via-white to-white border border-orange-primary/15 rounded-3xl p-8 md:p-12 shadow-card">
          <Quote className="absolute top-6 right-6 text-orange-primary/20" size={64} strokeWidth={1.5} />
          <div className="flex items-center gap-1 mb-4">
            {[...Array(t.rating || 5)].map((_, i) => (
              <Star key={i} size={18} className="text-orange-primary fill-orange-primary" />
            ))}
          </div>
          <p className="font-display text-xl md:text-2xl text-gray-dark leading-relaxed mb-6 italic">
            "{t.quote}"
          </p>
          <div className="flex items-center gap-4 pt-6 border-t border-orange-primary/15">
            {t.photo_url ? (
              <img src={t.photo_url} alt={t.name} className="w-14 h-14 rounded-full object-cover" />
            ) : (
              <div className="w-14 h-14 rounded-full gradient-orange flex items-center justify-center text-white font-bold text-lg" aria-hidden="true">
                {t.name.charAt(0)}
              </div>
            )}
            <div className="flex-1">
              <p className="font-display font-bold text-gray-dark text-lg">{t.name}</p>
              {subtitle && <p className="text-gray-medium text-sm">{subtitle}</p>}
            </div>
            {t.logo_url && (
              <img src={t.logo_url} alt={`${t.company || "Company"} logo`} className="h-10 w-auto object-contain opacity-80" />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustryTestimonial;
